import { useEffect, useMemo, useState } from "react";
import { useLocation } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import {
  ArrowLeft, Clock, Compass, Heart, Medal, PlayCircle, Search, Sparkles, CheckCircle2, BookOpen,
} from "lucide-react";
import { Mascot } from "@/components/lesson/Mascot";
import {
  courseLessonPath, levelBadge,
  type CourseWithEnrollment, type InterestOption, type RecommendationsPayload,
} from "@/lib/studentCourses";

interface CoursesPayload { courses: CourseWithEnrollment[] }
interface LearnerProfilePayload { interestOptions: InterestOption[] }

export default function Courses() {
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const [, setLocation] = useLocation();
  const [interestFilter, setInterestFilter] = useState<string>("all");
  const [search, setSearch] = useState("");

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, []);

  const { data: catalog, isLoading: coursesLoading } = useQuery<CoursesPayload>({
    queryKey: ["/api/courses"],
    enabled: true,
  });
  const { data: recommendations, isLoading: recsLoading } = useQuery<RecommendationsPayload>({
    queryKey: ["/api/recommendations"],
    enabled: true,
  });
  const { data: learner } = useQuery<LearnerProfilePayload>({
    queryKey: ["/api/profile/learner"],
    enabled: true,
  });

  const courses = catalog?.courses ?? [];
  const interestOptions = learner?.interestOptions ?? [];
  const continueCourse = recommendations?.continueCourse ?? null;
  const forYou = recommendations?.forYou ?? [];
  const interestsSet = recommendations?.interestsSet ?? true;

  // Only offer chips for interests that actually have courses behind them.
  const usableInterests = useMemo(() => {
    const tagged = new Set<string>();
    for (const course of courses) for (const tag of course.interestTags ?? []) tagged.add(tag);
    return interestOptions.filter(option => tagged.has(option.id));
  }, [courses, interestOptions]);

  const visibleCourses = useMemo(() => {
    const term = search.trim().toLowerCase();
    return courses.filter(course => {
      if (interestFilter !== "all" && !(course.interestTags ?? []).includes(interestFilter)) return false;
      if (!term) return true;
      return (
        course.title.toLowerCase().includes(term) ||
        (course.description ?? "").toLowerCase().includes(term) ||
        (course.subject ?? "").toLowerCase().includes(term) ||
        (course.credentialTitle ?? "").toLowerCase().includes(term)
      );
    });
  }, [courses, interestFilter, search]);

  const openCourse = (slug: string) => setLocation(`/courses/${slug}`);

  return (
    <div className="min-h-screen bg-background">
      <div className="bg-primary text-primary-foreground p-4 md:p-6">
        <div className="container max-w-5xl mx-auto">
          <Button
            variant="ghost"
            className="text-white hover:bg-white/20 p-2 mb-2"
            onClick={() => setLocation("/dashboard")}
            data-testid="button-courses-back-dashboard"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <h1 className="text-2xl sm:text-3xl font-extrabold flex items-center gap-2">
            <Compass className="w-7 h-7" /> Courses
          </h1>
          <p className="text-white/90 mt-1">
            Short courses built from lessons you can play. Finish one and you earn a certificate with your name on it.
          </p>
        </div>
      </div>

      <div className="container max-w-5xl mx-auto p-4 md:p-6 space-y-8">
        {/* ── Continue ── */}
        {recsLoading ? (
          <Skeleton className="h-32 rounded-2xl" data-testid="skeleton-continue" />
        ) : continueCourse ? (
          <section className="space-y-3">
            <h2 className="text-xl font-extrabold">Continue</h2>
            <Card
              className="rounded-2xl border-primary/30 bg-card animate-in fade-in slide-in-from-bottom-2 duration-500 fill-mode-both"
              data-testid="card-continue-course"
            >
              <CardContent className="p-4 md:p-6">
                <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                  <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center text-4xl flex-shrink-0">
                    {continueCourse.course.emoji || "📘"}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs uppercase tracking-wide font-bold text-primary">Pick up where you left off</p>
                    <h3 className="text-xl font-extrabold leading-tight">{continueCourse.course.title}</h3>
                    <p className="text-sm text-muted-foreground mt-1">
                      Next up: <span className="font-semibold text-foreground">{continueCourse.nextLesson.title}</span>
                    </p>
                    {continueCourse.course.enrollment && (
                      <div className="mt-3 max-w-sm">
                        <div className="flex justify-between text-xs text-muted-foreground mb-1">
                          <span>
                            {continueCourse.course.enrollment.completedLessons} of {continueCourse.course.enrollment.totalLessons} lessons done
                          </span>
                          <span>{Math.round(continueCourse.course.enrollment.progress)}%</span>
                        </div>
                        <Progress value={continueCourse.course.enrollment.progress} className="h-2" />
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col gap-2 sm:w-44">
                    <Button
                      className="rounded-xl font-bold"
                      onClick={() => setLocation(courseLessonPath(continueCourse.nextLesson.slug, continueCourse.course.slug))}
                      data-testid="button-continue-next-lesson"
                    >
                      <PlayCircle className="w-4 h-4 mr-2" /> Next lesson
                    </Button>
                    <Button
                      variant="outline"
                      className="rounded-xl"
                      onClick={() => openCourse(continueCourse.course.slug)}
                      data-testid="button-continue-view-course"
                    >
                      View course
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </section>
        ) : null}

        {/* ── For you ── */}
        <section className="space-y-3">
          <h2 className="text-xl font-extrabold">For you</h2>
          {recsLoading ? (
            <div className="grid sm:grid-cols-2 gap-4">
              <Skeleton className="h-44 rounded-2xl" />
              <Skeleton className="h-44 rounded-2xl" />
            </div>
          ) : !interestsSet ? (
            <Card className="rounded-2xl border-dashed border-primary/40 bg-primary/5" data-testid="card-interests-prompt">
              <CardContent className="p-5 flex flex-col sm:flex-row sm:items-center gap-4">
                <Mascot
                  mood="wave"
                  size="md"
                  className="flex-1"
                  message="Tell us what you love and we will pick courses just for you."
                />
                <Button
                  className="rounded-xl font-bold flex-shrink-0"
                  onClick={() => setLocation("/profile?tab=learner")}
                  data-testid="button-set-interests"
                >
                  <Heart className="w-4 h-4 mr-2" /> Tell us what you love
                </Button>
              </CardContent>
            </Card>
          ) : forYou.length === 0 ? (
            <Card className="rounded-2xl border-dashed">
              <CardContent className="p-6 text-center text-sm text-muted-foreground" data-testid="text-foryou-empty">
                No picks for you yet. Browse the courses below and start one you like.
              </CardContent>
            </Card>
          ) : (
            <div className="grid sm:grid-cols-2 gap-4">
              {forYou.map((item, i) => (
                <CourseCard
                  key={item.course.id}
                  course={item.course}
                  reasons={item.reasons}
                  delayIndex={i}
                  onOpen={() => openCourse(item.course.slug)}
                />
              ))}
            </div>
          )}
        </section>

        {/* ── All courses ── */}
        <section className="space-y-3">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <h2 className="text-xl font-extrabold flex-1">All courses</h2>
            <div className="relative sm:w-64">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search courses"
                className="pl-9 rounded-xl"
                aria-label="Search courses"
                data-testid="input-course-search"
              />
            </div>
          </div>

          {usableInterests.length > 0 && (
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => setInterestFilter("all")}
                className={`rounded-full px-4 py-1.5 text-sm font-semibold border-2 transition-colors ${
                  interestFilter === "all" ? "bg-primary text-primary-foreground border-primary" : "bg-card border-border hover:border-primary/50"
                }`}
                data-testid="filter-interest-all"
              >
                All
              </button>
              {usableInterests.map(option => (
                <button
                  key={option.id}
                  type="button"
                  onClick={() => setInterestFilter(prev => (prev === option.id ? "all" : option.id))}
                  className={`rounded-full px-4 py-1.5 text-sm font-semibold border-2 transition-colors ${
                    interestFilter === option.id ? "bg-primary text-primary-foreground border-primary" : "bg-card border-border hover:border-primary/50"
                  }`}
                  data-testid={`filter-interest-${option.id}`}
                >
                  <span className="mr-1">{option.emoji}</span>
                  {option.label}
                </button>
              ))}
            </div>
          )}

          {coursesLoading ? (
            <div className="grid sm:grid-cols-2 gap-4">
              {[0, 1, 2, 3].map(i => <Skeleton key={i} className="h-44 rounded-2xl" />)}
            </div>
          ) : courses.length === 0 ? (
            <Card className="rounded-2xl">
              <CardContent className="p-8 text-center">
                <Mascot mood="thinking" message="No courses are open just yet. Check back soon, we are building them now!" />
                <Button className="mt-4 rounded-xl" onClick={() => setLocation("/lessons")} data-testid="button-courses-go-lessons">
                  <BookOpen className="w-4 h-4 mr-2" /> Try a lesson instead
                </Button>
              </CardContent>
            </Card>
          ) : visibleCourses.length === 0 ? (
            <Card className="rounded-2xl border-dashed">
              <CardContent className="p-8 text-center space-y-3">
                <p className="text-sm text-muted-foreground" data-testid="text-courses-no-match">
                  No courses match that. Try another word or clear the filters.
                </p>
                <Button
                  variant="outline"
                  className="rounded-xl"
                  onClick={() => { setSearch(""); setInterestFilter("all"); }}
                  data-testid="button-clear-course-filters"
                >
                  Clear filters
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid sm:grid-cols-2 gap-4">
              {visibleCourses.map((course, i) => (
                <CourseCard key={course.id} course={course} delayIndex={i} onOpen={() => openCourse(course.slug)} />
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

const DELAY_CLASSES = ["", "delay-75", "delay-150", "delay-200", "delay-300"];

export function CourseCard({
  course,
  reasons,
  onOpen,
  delayIndex = 0,
}: {
  course: CourseWithEnrollment;
  reasons?: string[];
  onOpen: () => void;
  delayIndex?: number;
}) {
  const level = levelBadge(course.level);
  const enrollment = course.enrollment;
  const completed = !!enrollment?.completedAt;
  const delay = DELAY_CLASSES[Math.min(delayIndex, DELAY_CLASSES.length - 1)];

  return (
    <Card
      className={`hover-elevate cursor-pointer rounded-2xl transition-transform hover:-translate-y-0.5 h-full animate-in fade-in slide-in-from-bottom-2 duration-500 fill-mode-both ${delay}`}
      onClick={onOpen}
      role="button"
      tabIndex={0}
      onKeyDown={e => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onOpen(); } }}
      data-testid={`card-course-${course.slug}`}
    >
      <CardContent className="p-5 flex flex-col gap-3 h-full">
        <div className="flex items-start gap-3">
          <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center text-2xl flex-shrink-0">
            {course.emoji || "📘"}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="font-bold text-base leading-tight">{course.title}</h3>
              <Badge className={`border-0 ${level.className}`} data-testid={`badge-level-${course.slug}`}>{level.label}</Badge>
              {completed && (
                <Badge className="bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300 border-0">
                  <CheckCircle2 className="w-3 h-3 mr-1" /> Completed
                </Badge>
              )}
            </div>
            {course.description && <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{course.description}</p>}
          </div>
        </div>

        <div className="flex items-center gap-3 text-xs text-muted-foreground flex-wrap">
          <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {course.estimatedMinutes} min</span>
          <span className="flex items-center gap-1"><BookOpen className="w-3.5 h-3.5" /> {course.lessonCount} lesson{course.lessonCount === 1 ? "" : "s"}</span>
          <span className="flex items-center gap-1 text-primary font-semibold"><Sparkles className="w-3.5 h-3.5" /> {course.xpTotal} XP</span>
        </div>

        {course.credentialTitle && (
          <p className="flex items-center gap-1.5 text-xs font-semibold text-amber-700 dark:text-amber-300">
            <Medal className="w-4 h-4 text-amber-500 flex-shrink-0" />
            {completed ? `Earned: ${course.credentialTitle}` : `Earn: ${course.credentialTitle}`}
          </p>
        )}

        {reasons && reasons.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {reasons.slice(0, 3).map(reason => (
              <span
                key={reason}
                className="rounded-full bg-primary/10 text-primary text-[11px] font-semibold px-2.5 py-1"
                data-testid={`chip-reason-${course.slug}`}
              >
                {reason}
              </span>
            ))}
          </div>
        )}

        {enrollment && !completed && (
          <div className="mt-auto">
            <div className="flex justify-between text-xs text-muted-foreground mb-1">
              <span>{enrollment.completedLessons} of {enrollment.totalLessons} lessons done</span>
              <span>{Math.round(enrollment.progress)}%</span>
            </div>
            <Progress value={enrollment.progress} className="h-2" />
          </div>
        )}
      </CardContent>
    </Card>
  );
}
